import { getFunctions, httpsCallable } from "firebase/functions";
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  Unsubscribe,
} from "firebase/firestore";
import app from "../../services/firebaseApp";
import firestoreInstance from "../../services/firestoreInstance";
import {
  SpiddDraftMatch,
  SpiddDraftSummary,
} from "../../types/SpiddDraftMatch";

/** Callables SPIDD déployées en europe-west1 (comme spiddIngest). */
const spiddFunctions = getFunctions(app, "europe-west1");

function draftsCollection(tournoiId: string) {
  return collection(
    firestoreInstance,
    "tournois",
    tournoiId,
    "spiddDraftMatches"
  );
}

export function listenSpiddDraftMatches(
  tournoiId: string,
  onData: (drafts: SpiddDraftMatch[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const q = query(draftsCollection(tournoiId), orderBy("sourceTableauId"));
  return onSnapshot(
    q,
    (snap) => {
      const drafts = snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as Omit<SpiddDraftMatch, "id">),
      }));
      onData(drafts);
    },
    (err) => onError?.(err)
  );
}

export function summarizeDrafts(drafts: SpiddDraftMatch[]): SpiddDraftSummary {
  const summary: SpiddDraftSummary = {
    total: drafts.length,
    proposed: 0,
    needsReview: 0,
    approved: 0,
    linked: 0,
    ignored: 0,
    stale: 0,
    conflicts: 0,
  };
  for (const d of drafts) {
    switch (d.status) {
      case "PROPOSED":
        summary.proposed += 1;
        break;
      case "NEEDS_REVIEW":
        summary.needsReview += 1;
        break;
      case "APPROVED":
        summary.approved += 1;
        break;
      case "LINKED":
        summary.linked += 1;
        break;
      case "IGNORED":
        summary.ignored += 1;
        break;
      case "STALE":
        summary.stale += 1;
        break;
      default:
        break;
    }
    if (d.matchSuggestion === "CONFLICT") summary.conflicts += 1;
  }
  return summary;
}

export async function callSpiddDraftCreateMatch(params: {
  tournoiId: string;
  draftId: string;
  expectedRevision?: number;
}): Promise<{ ok: boolean; matchId: string; deduped?: boolean }> {
  const fn = httpsCallable(spiddFunctions, "spiddDraftCreateMatch");
  const result = await fn(params);
  return result.data as { ok: boolean; matchId: string; deduped?: boolean };
}

export async function callSpiddDraftLinkMatch(params: {
  tournoiId: string;
  draftId: string;
  matchId: string;
  expectedRevision?: number;
}): Promise<{ ok: boolean; matchId: string; deduped?: boolean }> {
  const fn = httpsCallable(spiddFunctions, "spiddDraftLinkMatch");
  const result = await fn(params);
  return result.data as { ok: boolean; matchId: string; deduped?: boolean };
}

export async function callSpiddDraftIgnore(params: {
  tournoiId: string;
  draftId: string;
}): Promise<{ ok: boolean }> {
  const fn = httpsCallable(spiddFunctions, "spiddDraftIgnore");
  const result = await fn(params);
  return result.data as { ok: boolean };
}

export async function callSpiddDraftUnlink(params: {
  tournoiId: string;
  draftId: string;
  reason?: string;
}): Promise<{ ok: boolean }> {
  const fn = httpsCallable(spiddFunctions, "spiddDraftUnlink");
  const result = await fn(params);
  return result.data as { ok: boolean };
}

export async function callSpiddDraftUpdateGrouping(params: {
  tournoiId: string;
  draftId: string;
  groups: string[][];
}): Promise<{ ok: boolean; draftIds: string[] }> {
  const fn = httpsCallable(spiddFunctions, "spiddDraftUpdateGrouping");
  const result = await fn(params);
  return result.data as { ok: boolean; draftIds: string[] };
}

export async function callSpiddDraftBatchCreate(params: {
  tournoiId: string;
  draftIds: string[];
}): Promise<{
  ok: boolean;
  created: Array<{ draftId: string; matchId: string; deduped?: boolean }>;
  errors: Array<{ draftId: string; code: string; message: string }>;
}> {
  const fn = httpsCallable(spiddFunctions, "spiddDraftBatchCreate");
  const result = await fn(params);
  return result.data as {
    ok: boolean;
    created: Array<{ draftId: string; matchId: string; deduped?: boolean }>;
    errors: Array<{ draftId: string; code: string; message: string }>;
  };
}

export async function callSpiddDraftBatchRemap(params: {
  tournoiId: string;
  draftIds: string[];
  firestoreTableauId?: string;
  firestoreTourId?: string;
}): Promise<{
  ok: boolean;
  updated: Array<{
    draftId: string;
    firestoreTableauId: string | null;
    firestoreTourId: string | null;
    status: string;
    matchSuggestion?: string;
  }>;
  errors: Array<{ draftId: string; code: string; message: string }>;
}> {
  const fn = httpsCallable(spiddFunctions, "spiddDraftBatchRemap");
  const result = await fn(params);
  return result.data as {
    ok: boolean;
    updated: Array<{
      draftId: string;
      firestoreTableauId: string | null;
      firestoreTourId: string | null;
      status: string;
      matchSuggestion?: string;
    }>;
    errors: Array<{ draftId: string; code: string; message: string }>;
  };
}

export async function callSpiddDraftSyncRun(params: {
  tournoiId: string;
  tableauId?: string;
}): Promise<{ ok: boolean; results: unknown[] }> {
  const fn = httpsCallable(spiddFunctions, "spiddDraftSyncRun");
  const result = await fn(params);
  return result.data as { ok: boolean; results: unknown[] };
}
